/**
 * Editor diagnostics for the navigation preview: the wording alone, drawn by
 * `notices.jsx`, which opens each row with the mark of its tone.
 *
 * Every message is untranslated — a shopper must never see one.
 */

/**
 * CMS titles carry inline markup — `<product>` and the like — which the panes
 * render through `RichText`. Editor chrome is plain text, so strip it.
 *
 * @param {*} value - The authored string, or anything else.
 * @returns {string} The text with tags removed, or an empty string.
 */
export const plainText = (value) =>
    typeof value === 'string' ? value.replace(/<[^>]*>/g, '').trim() : ''

/**
 * Names an entry in an editor message, falling through the fields the entry
 * types carry down to the type itself.
 *
 * @param {Object} item - Any navigation entry, teaser or separator.
 * @returns {string}
 */
export const entryLabel = (item) =>
    plainText(item?.title) ||
    plainText(item?.tag) ||
    plainText(item?.text) ||
    item?.type ||
    'untitled'

/**
 * Unwraps a CMS element, which arrives as `{item}` at most levels and bare
 * inside a level-3 subtree.
 *
 * @param {Object} entry - The authored element.
 * @returns {Object|undefined} The entry itself.
 */
export const unwrap = (entry) => (entry?.type ? entry : entry?.item)

/**
 * Splits entries by the viewport their `hide` flag takes them off, so that a
 * hidden entry is not read as a broken one.
 *
 * @param {Array} items - Entries, teasers and separators, already unwrapped.
 * @returns {{mobile: string[], desktop: string[]}} Labels of what is hidden at
 *   each viewport.
 */
export const hiddenLabels = (items = []) =>
    items.filter(Boolean).reduce(
        (acc, item) => {
            // the two hiding flags by name, rather than indexing the
            // accumulator with authored data
            if (item.hide === 'mobile' || item.hide === 'desktop') {
                acc[item.hide].push(entryLabel(item))
            }
            return acc
        },
        {mobile: [], desktop: []}
    )

/**
 * The line a hidden-entry notice shows. Reads "here" because the notice renders
 * only at the viewport the entries are hidden on.
 *
 * @param {string[]} labels - Labels of the hidden entries.
 * @returns {string[]} One message, or none when nothing is hidden.
 */
export const hiddenMessages = (labels) =>
    labels.length ? [`hidden here: ${labels.join(', ')}`] : []

/** A level-1 entry pointing at a level-2 module that is not on the page. */
export const missingLevel2Message = (item) =>
    `level-1 "${entryLabel(item)}" cannot open — level2ModuleId "${
        item?.level2ModuleId ?? 'none'
    }" matches no level-2 module on this page.`

/** A level-2 module no level-1 entry points at, so its breadcrumb has no title. */
export const unmatchedLevel2Message = (level2ModuleId) =>
    `no level-1 entry points at level2ModuleId "${
        level2ModuleId ?? 'none'
    }" — this pane's breadcrumb has no title.`

/**
 * More than one level-1 module on the page: they write the same cache entry, so
 * only the last drives the flyout, and no pane looks broken.
 *
 * @param {number} count - How many are on the page.
 * @returns {string}
 */
export const multipleLevel1ModulesMessage = (count) =>
    `this page has ${count} leap-navigation-level1 modules — only one can drive the header's flyout, and the last one on the page wins.`

/** No level-1 module at all, so every level-2 breadcrumb blanks at once. */
export const noLevel1ModuleMessage = () =>
    `no leap-navigation-level1 module on this page — no level-2 pane can resolve its parent title.`

/**
 * A level-2 module more than one level-1 entry points at: it carries one
 * `parentTitle`, so its breadcrumb names the category that claimed it last.
 *
 * @param {string[]} titles - The claiming entries' titles, in page order.
 * @returns {string}
 */
export const multipleParentsMessage = (titles = []) =>
    `${titles.length} level-1 entries point at this module — ${titles
        .map((title) => `"${plainText(title)}"`)
        .join(', ')} — and only the last one's title reaches its breadcrumb.`

/** A level-3 subtree of a type the preview has no component for. */
export const unsupportedLevel3Message = (item) =>
    `level-2 "${entryLabel(item)}" opens level-3 type "${
        item?.nextLevel?.type ?? 'none'
    }", which renders nothing.`

/** A level-2 entry with nothing to open and nothing to follow. */
export const deadEndMessage = (item) =>
    `level-2 "${entryLabel(
        item
    )}" has neither a level-3 subtree nor a link — clicking it does nothing.`

/** Level-1 teasers are authored the same at both viewports but shown at one. */
export const level1TeasersMobileOnlyMessage = () =>
    `level-1 teasers show on mobile only — narrow the preview to see them.`

/**
 * The level-1 module seeds the real flyout as well as its own panes, so the
 * drill-in, the animation and the panel's real room are one click away.
 */
export const headerFlyoutPreviewMessage = () =>
    `the header's flyout previews this tree too — open it from the bar at the top of the page to see it as a shopper does.`

/**
 * Everything `leap-header` authors lives in the bar's `nav` slot, which the
 * header recipe takes off at `base`.
 */
export const headerDesktopOnlyMessage = () =>
    `leap-header's link row shows on desktop only — widen the preview to see it.`

/** The header module renders in the app shell, not where it sits in the page. */
export const headerInShellMessage = () =>
    `leap-header renders in the bar at the top of the page, not here.`
